import { useState, useEffect, useCallback } from 'react';

import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';

import useEnv from '@zenbusiness/application-commons-env';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useIntercom } from '@zenbusiness/application-commons-intercom';
import { ErrorBoundary } from '@zenbusiness/application-commons-logging';
import Banner from '@zenbusiness/zui/Banner';
import Stack from '@zenbusiness/zui/Stack';

import { ErrorStateSection } from '#client/components/ErrorStateSection';
import { HomeBanners } from '#client/components/HomeBanners';
import { NextUp } from '#client/components/NextUp';
import { OrderTracker } from '#client/components/OrderTracker';
import { PersonalizedModal } from '#client/components/PersonalizedModal';
import { ProfileHeader } from '#client/components/ProfileHeader';
import { ENABLE_DASHBOARD_CAROUSEL, ENABLE_SIMPLE_ORDER_TRACKER } from '#client/constants/experimentsFlags';
import { ZenDash, EducationUi } from '#client/directEntry/MFEs';
import { useOrderTrackerStatus } from '#client/hooks/useOrderTrackerStatus';
import useQueryParams from '#client/hooks/useQueryParams';
import { PRODUCT_TOUR_COOKIE } from '#client/utils/constants';
import { getCookie, setCookie } from '#client/utils/cookies';

import { Carousel } from './Carousel';
import { MODAL_PARAM_KEY, STORY_MODAL_PARAM_KEY } from './constants';
import { StyledPageContainer } from './Home.styles';
import getModalDetails, { allowedModals } from './modals';
import { ProductCards } from './ProductCards';
import DashboardWidgets from '../../components/Widgets/DashboardWidgets';

const Home = ({
  account,
  businessEntity,
  entitlements,
  loading,
  pricingDeviations
}) => {
  const { INTERCOM_PRODUCT_TOUR_ID = 314770 } = useEnv();
  const { overrideProductTourId } = useQueryParams();
  const currentProductTourId = overrideProductTourId || INTERCOM_PRODUCT_TOUR_ID;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentModal, setCurrentModal] = useState(null);
  const [modalProps, setModalProps] = useState({});
  const [fullScreen, setFullScreen] = useState(false);

  // TODO: Remove this once Texas filing portal is back online
  const { orderTasksWithStatus } = useOrderTrackerStatus();

  const productTourShowed = getCookie(PRODUCT_TOUR_COOKIE) === 'true';

  const [searchParams, setSearchParams] = useSearchParams();
  const { startTour } = useIntercom();
  const {
    businessEntityUuid = null, businessEntityType = null,
    businessEntityData
  } = businessEntity || {};
  const isStoryModal = !!searchParams.get(STORY_MODAL_PARAM_KEY);
  const showSimpleOrderTracker = useExperimentFlag(ENABLE_SIMPLE_ORDER_TRACKER);
  const enableCarousel = useExperimentFlag(ENABLE_DASHBOARD_CAROUSEL, false);

  // TODO: Remove this once Texas filing portal is back online
  const formationTask = orderTasksWithStatus?.find((task) => {
    const type = task.type.split('_');
    return type[1] === 'formation';
  });
  const formationTaskStatus = formationTask?.status;

  const shouldShowTexasFilingBanner = businessEntityData?.domesticJurisdiction === 'TX' && (formationTaskStatus === 'incomplete' || formationTaskStatus === 'pending');

  const showProductTour = useCallback(
    (ptShowed, tourId) => {
      if (!ptShowed) {
        startTour(tourId);
        setCookie(PRODUCT_TOUR_COOKIE, true);
      }
    },
    [startTour]
  );

  const handleOpenModal = useCallback(
    (modal, incomingModalProps = {}) => {
      const modalConfig = getModalDetails(modal, {});
      setFullScreen(modalConfig?.fullScreen ?? false);
      setCurrentModal(modal);
      setModalProps(incomingModalProps);
      setIsModalOpen(true);
      setSearchParams((params) => {
        params.set(MODAL_PARAM_KEY, modal);
        return params;
      });
    },
    [setSearchParams]
  );

  const handleCloseModal = useCallback(
    () => {
      setIsModalOpen(false);
      setSearchParams((params) => {
        params.delete(MODAL_PARAM_KEY);
        return params;
      });
    },
    [setSearchParams]
  );

  useEffect(() => {
    const modalName = searchParams.get(MODAL_PARAM_KEY);
    if (
      !allowedModals.includes(modalName)
      || !modalName
      || !businessEntityUuid
      || isModalOpen
    ) {
      return;
    }
    handleOpenModal(modalName, {});
  }, [businessEntityUuid, handleOpenModal, searchParams, isModalOpen]);

  // Need to simplify product tour condition check
  const getFormationEntitlement = entitlements.get(
    `${businessEntityType}_formation`
  );
  const formationEntitlement = getFormationEntitlement?.fulfillment;
  const postPurchaseComplete = formationEntitlement?.orderTaskStatus === 'complete';
  const productModalCondition = !loading && !postPurchaseComplete && !productTourShowed;

  useEffect(() => {
    if (!loading && productModalCondition && !currentModal) {
      showProductTour(productTourShowed, currentProductTourId);
    }
  }, [
    currentModal,
    currentProductTourId,
    loading,
    productModalCondition,
    productTourShowed,
    showProductTour
  ]);

  return (
    <StyledPageContainer>
      <HomeBanners onOpenModal={handleOpenModal} />
      <Stack spacing={{ xs: 2, sm: 3 }}>
        {shouldShowTexasFilingBanner && (
        <Banner
          clickstreamProps={{ appSection: 'home' }}
          title="Notice: Texas Filing Delay"
          subtitle="Texas Secretary of State filing portal was recently closed for several days. Orders for Texas could be delayed as they work to catch up. We appreciate your patience."
          variant="alert"
        />
        )}
        <ErrorBoundary id="src/Home#ProfileHeader" fallback={<ErrorStateSection />}>
          <ProfileHeader businessEntityUuid={businessEntityUuid} />
        </ErrorBoundary>

        <ErrorBoundary id="src/Home#NextUp" fallback={<ErrorStateSection title="Next Up" />}>
          <NextUp />
        </ErrorBoundary>

        <ErrorBoundary id="src/Home#OrderStatus" fallback={<ErrorStateSection title="Order Status" />}>
          {showSimpleOrderTracker ? (
            <OrderTracker />
          ) : (
            <ZenDash exportName="OrderStatus" />
          )}
        </ErrorBoundary>

        {!enableCarousel && (
        <ErrorBoundary id="src/Home#SMSOptIn" fallback={<ErrorStateSection />}>
          <ZenDash exportName="SMSOptIn" />
        </ErrorBoundary>
        )}

        <ErrorBoundary id="src/Home#Carousel" fallback={<ErrorStateSection />}>
          <Carousel />
        </ErrorBoundary>

        <ErrorBoundary id="src/Home#Widgets" fallback={<ErrorStateSection />}>
          <DashboardWidgets />
        </ErrorBoundary>

        <ErrorBoundary id="src/Home#ProductCards" fallback={<ErrorStateSection title="Products for your business" />}>
          <ProductCards
            handleOpenModal={handleOpenModal}
            title="Products for your business"
          />
        </ErrorBoundary>
      </Stack>

      {/* Checking currentModal and story modal is not populated to make sure
      deep link works for all existing modals */}
      {!loading && !currentModal && !isStoryModal && (
        <ErrorBoundary id="src/Home#PersonalizedModal" fallback={<ErrorStateSection />}>
          <PersonalizedModal />
        </ErrorBoundary>
      )}
      {!loading && currentModal && (
        <ZenDash
          exportName="ProductModalWrapper"
          account={account}
          businessEntity={businessEntity}
          currentModal={currentModal}
          entitlements={entitlements}
          fullScreen={fullScreen}
          handleCloseModal={handleCloseModal}
          handleOpenModal={handleOpenModal}
          isModalOpen={isModalOpen}
          modalProps={modalProps}
          pricingDeviations={pricingDeviations}
        />
      )}
      <EducationUi exportName="EducationStudioStoryViewer" />

    </StyledPageContainer>
  );
};

Home.propTypes = {
  account: PropTypes.shape({
    firstName: PropTypes.string
  }),
  entitlements: PropTypes.shape({
    has: PropTypes.func.isRequired,
    all: PropTypes.func.isRequired,
    get: PropTypes.func.isRequired
  }).isRequired,
  businessEntity: PropTypes.shape({
    businessEntityType: PropTypes.string,
    businessEntityUuid: PropTypes.string,
    businessEntityData: PropTypes.shape({
      domesticJurisdiction: PropTypes.string
    })
  }),
  loading: PropTypes.bool,
  pricingDeviations: PropTypes.shape({}).isRequired
};

export default Home;
